import type { RepositoryFile } from '@trove/core';
import { extractWikiLinks } from './markdown';

export interface VaultSearchHit {
  path: string;
  snippet: string;
  score: number;
}

export function searchVaultFiles(
  files: RepositoryFile[],
  query: string,
  limit = 40,
): VaultSearchHit[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const hits: VaultSearchHit[] = [];
  for (const file of files) {
    const path = file.path;
    const content = file.content ?? '';
    const pathMatch = path.toLowerCase().includes(needle);
    const index = content.toLowerCase().indexOf(needle);
    if (!pathMatch && index < 0) continue;
    const start = Math.max(0, index < 0 ? 0 : index - 40);
    const end = Math.min(content.length, index < 0 ? 80 : index + needle.length + 40);
    hits.push({
      path,
      snippet: (index < 0 ? content.slice(0, 80) : content.slice(start, end)).replaceAll('\n', ' '),
      score: (pathMatch ? 10 : 0) + (index >= 0 ? 5 : 0),
    });
  }
  return hits
    .sort((left, right) => right.score - left.score || left.path.localeCompare(right.path))
    .slice(0, limit);
}

export function resolveWikiTarget(files: RepositoryFile[], target: string): string | null {
  const normalized = target.trim().replace(/\\/g, '/');
  const exact = files.find((file) => file.path === normalized || file.path === `${normalized}.md`);
  if (exact) return exact.path;
  const byTitle = files.find((file) => {
    const base = file.path.split('/').pop() ?? '';
    const stem = base.replace(/\.md$/i, '');
    return (
      stem.toLowerCase() === normalized.toLowerCase() ||
      base.toLowerCase() === normalized.toLowerCase()
    );
  });
  return byTitle?.path ?? null;
}

export function backlinksTo(files: RepositoryFile[], path: string): string[] {
  const stem = (path.split('/').pop() ?? path).replace(/\.md$/i, '');
  const aliases = new Set([path, stem, path.replace(/\.md$/i, '')]);
  const sources: string[] = [];
  for (const file of files) {
    if (file.path === path) continue;
    const links = extractWikiLinks(file.content ?? '');
    if (links.some((link) => aliases.has(link) || aliases.has(link.replace(/\.md$/i, '')))) {
      sources.push(file.path);
    }
  }
  return sources;
}
